import { DateTime } from 'luxon';
import { toast } from 'sonner';
import { Dispatch } from 'react';
import { Burst, ScoutLocation } from '@/types/location';
import { Button } from '@/components/shadcn/button';
import { validateOrganizationFunded } from '@/api/organization';
import * as Toast from '@/components/ui/toast/Toast';
import { BURST_PRICE } from '@/components/ui/scout/Scout';
import { useMapCredits } from '@/hooks/useMapCredits';
import { useScout } from '@/hooks/useScout';
import { useUser } from '@/hooks/useUser';
import MoreOptionsKebab from '../MoreOptionsKebab';
import RemoveLocation from '../RemoveLocation';

interface Props {
  requestCost: number | null;
  latest?: Burst;
  hasActiveBurst: boolean;
  location: ScoutLocation;
  setBurstModalOptions: Dispatch<{
    isOpen: boolean;
    locationsIds: string[];
    successCallback: () => void;
  }>;
}

export default function Info({
  requestCost,
  latest,
  hasActiveBurst,
  location,
  setBurstModalOptions,
}: Props) {
  const { setCreditsModalOptions } = useMapCredits();

  const { setLocations, setActiveSequenceIndex, setActiveFrameIndex } =
    useScout();
  const { user } = useUser();

  const handleAddBurst = async (id: string) => {
    const response = await validateOrganizationFunded(
      user?.currentOrganization,
    );

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="Something went wrong, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    if (response.credits >= BURST_PRICE) {
      setBurstModalOptions({
        isOpen: true,
        locationsIds: [id],
        successCallback: () => {},
      });
    } else {
      toast.custom(t => (
        <Toast.Default
          title="Map Credits Needed"
          description="Purchase map credits to continue using Scout."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Purchase Credits',
              onClick: () => {
                setCreditsModalOptions({ isVisible: true });
                toast.dismiss(t);
              },
              variant: 'default',
              size: 'fw',
            },
          ]}
        />
      ));
    }
  };

  return (
    <div className="flex w-full items-center gap-4">
      {requestCost !== null && requestCost > 0 && (
        <div className="inline-flex h-[30px] w-[62px] items-center justify-center gap-2 rounded-full border border-azure-400 px-4 py-2">
          <div className="text-xs font-bold">Fresh</div>
        </div>
      )}
      {latest && hasActiveBurst ? (
        <div className="inline-flex h-[30px] items-center justify-center gap-2 rounded-full border border-harvestGoldWarning-600 px-4 py-2">
          <div className="text-xs font-bold">
            Burst expires{' '}
            {DateTime.fromISO(latest.validUntil, {
              zone: 'utc',
            }).toFormat('LLL dd, yyyy')}
          </div>
        </div>
      ) : (
        <Button variant="burst" onClick={() => handleAddBurst(location._id)}>
          Burst
        </Button>
      )}
      <MoreOptionsKebab
        elements={[
          <RemoveLocation
            key={location._id}
            id={location._id}
            setLocations={setLocations}
            callback={() => {
              setActiveSequenceIndex(0);
              setActiveFrameIndex({ value: 0 });
            }}
          />,
        ]}
      />
    </div>
  );
}
